import { useEffect, useState } from "react";
import { Download, Share, Plus } from "lucide-react";
import {
  hasNativePrompt,
  triggerNativePrompt,
  isIosSafari,
  isStandalone,
  isInstallDismissed,
  dismissInstall,
  subscribe,
} from "../lib/install-prompt";

/** Floating install affordance: a compact, translucent icon button
 *  stacked just above the QuickCapture FAB on the bottom-right so the
 *  two never overlap. Visible across all tabs. Surfaces:
 *    - the captured beforeinstallprompt on Android Chrome / Edge /
 *      Brave (one tap -> Chrome's install dialog)
 *    - an iOS Safari instruction card with Share -> Add to Home Screen
 *      illustrated, since iOS doesn't fire the event
 *
 *  Hides when:
 *    - already running standalone
 *    - the user dismissed within the 14-day TTL
 *    - neither path is available (other browsers, in-app webviews) */
export function InstallButton() {
  const [, force] = useState(0);
  const [showIos, setShowIos] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => subscribe(() => force((n) => n + 1)), []);

  const standalone = isStandalone();
  const dismissed = isInstallDismissed();
  const native = hasNativePrompt();
  const ios = isIosSafari();

  if (standalone || dismissed) return null;
  if (!native && !ios) return null;

  const handleClick = async () => {
    if (native) {
      setBusy(true);
      try {
        const outcome = await triggerNativePrompt();
        if (outcome === "dismissed") dismissInstall();
      } finally {
        setBusy(false);
      }
    } else if (ios) {
      setShowIos((v) => !v);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        aria-label="Install Memso to home screen"
        title="Install Memso"
        className="fixed bottom-[9.5rem] md:bottom-24 right-4 z-40 inline-flex w-11 h-11 items-center justify-center rounded-full backdrop-blur-md bg-white/40 dark:bg-slate-900/40 border border-white/40 dark:border-slate-600/40 shadow-lg text-slate-900 dark:text-slate-100 hover:bg-white/60 dark:hover:bg-slate-900/60 transition-colors"
      >
        <Download className="w-5 h-5" />
      </button>

      {showIos && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIos(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 p-5 max-w-sm w-full space-y-4"
          >
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-400 to-indigo-500 flex items-center justify-center text-white shadow-md">
                <Download className="w-4 h-4" />
              </div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                Install Memso
              </h2>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              On iPhone Safari, add this to your home screen so it launches like
              a real app:
            </p>
            <ol className="space-y-2.5 text-sm text-slate-700 dark:text-slate-200">
              <li className="flex items-center gap-2.5">
                <span className="inline-flex w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-700 items-center justify-center text-xs font-bold">
                  1
                </span>
                <Share className="w-4 h-4 text-sky-500" />
                <span>Tap the Share button at the bottom of Safari.</span>
              </li>
              <li className="flex items-center gap-2.5">
                <span className="inline-flex w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-700 items-center justify-center text-xs font-bold">
                  2
                </span>
                <Plus className="w-4 h-4 text-emerald-500" />
                <span>Pick "Add to Home Screen" from the list.</span>
              </li>
              <li className="flex items-center gap-2.5">
                <span className="inline-flex w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-700 items-center justify-center text-xs font-bold">
                  3
                </span>
                <span>Tap "Add" in the top right.</span>
              </li>
            </ol>
            <div className="flex items-center justify-between gap-2 pt-1">
              <button
                type="button"
                onClick={() => {
                  dismissInstall();
                  setShowIos(false);
                }}
                className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
              >
                Don't remind me
              </button>
              <button
                type="button"
                onClick={() => setShowIos(false)}
                className="rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-3 py-1.5"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
